import { type FieldValues, useController } from 'react-hook-form'
import {
  type ComponentProps,
  type ComponentRef,
  forwardRef,
  type Ref,
  useImperativeHandle,
  useRef,
} from 'react'
import InputSsn from '@/src/common/components/Input/InputSsn'
import { sardineIdKey } from '@/src/services/Sardine/SardineId'
import { useEvent } from 'tamagui'
import { useSardine } from '@/src/services/Sardine'
import type { TextInput } from 'react-native'
import type {
  FormInputProps,
  FormInputType,
} from '@/src/experiences/card-application/components/form-input'

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const FormInputSsn = forwardRef(function FormInputSsn<T extends FieldValues>(
  { control, name, sardineId, onFocus, onBlur, ...props }: FormInputProps<T>,
  ref: Ref<TextInput | null>,
) {
  const { trackTextChange, trackFocusChange } = useSardine()
  const { field, fieldState, formState } = useController({ control, name })
  const localRef = useRef<TextInput>(null)
  const info = props.info ?? {}
  // TODO: Segment Integration

  // @ts-expect-error type differences between Ref.current and Ref
  // going to ignore for now
  useImperativeHandle(ref, () => localRef.current)
  useImperativeHandle(field.ref, () => localRef.current)

  const handleChange: NonNullable<ComponentProps<typeof InputSsn>['onChange']> = useEvent(
    (...args) => {
      field.onChange(...args)
      if (sardineId) {
        void trackTextChange(sardineId, args[0])
      }
    },
  )

  const handleFocus: NonNullable<ComponentProps<typeof InputSsn>['onFocus']> = useEvent((e) => {
    if (sardineId) {
      void trackFocusChange(sardineId, true)
    }
    onFocus?.(e)
  })

  const handleBlur: NonNullable<ComponentProps<typeof InputSsn>['onBlur']> = useEvent((e) => {
    if (sardineId) {
      void trackFocusChange(sardineId, false)
    }
    onBlur?.(e)
    field.onBlur()
  })

  return (
    <InputSsn
      {...props}
      {...(sardineId ? { dataSet: { [sardineIdKey]: sardineId } } : {})}
      value={field.value}
      info={{
        ...info,
        error: formState.isValidating ? undefined : fieldState.error?.message,
      }}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      ref={localRef}
    />
  )
}) as FormInputType<ComponentRef<typeof InputSsn>>
